import type { Lang } from './dict';
import { marketingCopy } from '../marketing/copy';
import { GUIDES } from '../marketing/guides';
import { matchMarketingRoute, routeAlternates } from '../marketing/routes';
import { productVideo, videoStructuredData } from '../marketing/productVideoContent';

export const SITE = 'https://mindstreamapp.ai';
export const OG_IMAGE = `${SITE}/images/og-buyer-radar.png`;
export const OG_IMAGE_WIDTH = '1200';
export const OG_IMAGE_HEIGHT = '630';

export interface LocaleSeo {
  title: string;
  description: string;
  imageAlt: string;
  ogLocale: string;
  htmlLang: string;
}
export interface PageSeo extends LocaleSeo {
  alternates?: { lang: string; href: string }[];
  canonical: string;
  standalone: boolean;
  robots?: string;
  jsonLd?: Record<string, unknown>;
  image?: string;
}
export const SEO: Record<Lang, LocaleSeo> = {
  en: {
    title: 'Mindstream — Find Better-Fit B2B Customers',
    description: 'Find relevant companies, check the evidence and prepare your next buyer conversation. Buyer Radar helps trade and export teams develop new business.',
    imageAlt: 'Mindstream — Buyer Radar', ogLocale: 'en_US', htmlLang: 'en',
  },
  zh: {
    title: 'Mindstream — 找到高质量新客户，持续跟进',
    description: 'Buyer Radar 帮助贸易与外贸团队发现匹配公司、核对依据、寻找联系人并准备开发信。从找客户到下一步，在同一个工作台里完成。',
    imageAlt: 'Mindstream — Buyer Radar', ogLocale: 'zh_CN', htmlLang: 'zh-Hans',
  },
};
export function canonicalFor(lang: Lang): string { return lang === 'zh' ? `${SITE}/zh/` : `${SITE}/`; }
export function alternateLocaleFor(lang: Lang): string { return lang === 'zh' ? 'en_US' : 'zh_CN'; }
export function langFromPath(pathname: string): Lang { return pathname.startsWith('/zh') ? 'zh' : 'en'; }
export function stripLocaleClusterTags(html: string): string {
  return html
    .replace(/[^\S\n]*<link[^>]*rel="alternate"[^>]*hreflang="[^"]*"[^>]*>\n?/gi, '')
    .replace(/[^\S\n]*<script[^>]*type="application\/ld\+json"[^>]*>[\s\S]*?<\/script>\n?/gi, '');
}
export function seoForPath(pathname: string): PageSeo {
  if (pathname.replace(/\/$/, '') === '/privacy') {
    return { ...SEO.en, title: 'Privacy Policy — Mindstream',
      description: 'How Mindstream collects, uses and protects your data, and the controls available to you.',
      canonical: `${SITE}/privacy/`, standalone: true };
  }
  const route = matchMarketingRoute(pathname);
  if (!route) return { ...SEO.en, title: 'Mindstream — Workspace & Account',
    canonical: `${SITE}/`, standalone: true, robots: 'noindex, nofollow' };
  const copy = marketingCopy(route.locale);
  const guide = GUIDES.find(item => item.slug === route.slug);
  const article = guide?.content[route.locale];
  const titles = {
    home: route.locale === 'en' ? SEO.en.title : SEO.zh.title,
    product: route.locale === 'en' ? 'Buyer Radar — AI Buyer Research for Export Teams | Mindstream'
      : route.locale === 'zh-Hant' ? 'Buyer Radar — 外貿買家研究與開發 | Mindstream' : 'Buyer Radar — 外贸买家研究与开发 | Mindstream',
    examples: `${copy.examples} — Buyer Radar | Mindstream`,
    demo: route.locale === 'en' ? 'Buyer Radar Demo — Discover, Act & Monitor | Mindstream'
      : route.locale === 'zh-Hant' ? 'Buyer Radar 產品演示 — 發現、跟進與監測 | Mindstream' : 'Buyer Radar 产品演示 — 发现、跟进与监测 | Mindstream',
    service: `${copy.service} | Mindstream`,
    extension: route.locale === 'en' ? 'Mindstream Chrome Extension — Your Gmail Customer Assistant'
      : 'Mindstream Chrome Extension — Gmail 客户邮件助手',
    insights: `${copy.insights} | Mindstream`,
    article: `${article?.title ?? copy.insights} | Mindstream`,
  };
  const descriptions = {
    home: copy.homeBody, product: copy.productBody, examples: copy.proofBody, service: copy.serviceBody,
    demo: productVideo(route.locale).description,
    extension: copy.extensionBody, insights: copy.insightsBody, article: article?.description ?? copy.insightsBody,
  };
  const canonical = `${SITE}${route.path}`;
  const alternates: { lang: string; href: string }[] = routeAlternates(route).map(item => ({ lang: item.locale, href: `${SITE}${item.path}` }));
  alternates.push({ lang: 'x-default', href: alternates[0].href });
  const organization = { '@type': 'Organization', name: 'Mindstream', url: `${SITE}/`,
    logo: `${SITE}/images/mindstream_logomark.png` };
  return {
    title: titles[route.page], description: descriptions[route.page],
    image: route.page === 'extension' ? `${SITE}/images/og-cover.png` : OG_IMAGE,
    imageAlt: route.page === 'extension' ? 'Mindstream Chrome Extension — Gmail customer assistant' : 'Mindstream — Buyer Radar',
    htmlLang: route.locale, ogLocale: route.locale === 'en' ? 'en_US' : route.locale === 'zh-Hant' ? 'zh_HK' : 'zh_CN',
    canonical, standalone: route.page !== 'home', alternates, robots: 'index, follow',
    ...(route.page === 'demo' ? { jsonLd: videoStructuredData(route.locale, canonical) }
      : route.page === 'article' && guide && article ? { jsonLd: {
      '@context': 'https://schema.org', '@type': 'Article', headline: article.title, description: article.description,
      datePublished: guide.date, dateModified: guide.date, inLanguage: route.locale, mainEntityOfPage: canonical,
      author: organization, publisher: organization, image: OG_IMAGE,
    } } : route.page === 'home' ? { jsonLd: { '@context': 'https://schema.org', ...organization } } : {}),
  };
}
